import DOMPurify from 'dompurify';
import { HTMLAttributes, useMemo } from 'react';
import { cn } from '@/lib/utils.ts';
import { CD_DOC_CLASS } from '@/lib/templateStyles/documentBaseStyles.ts';

// Force `rel="noopener noreferrer"` on every `target="_blank"` link in
// sanitized output. Without this, an admin clicking a link in a template
// preview could be hit with a reverse-tabnabbing attack (the opened tab gets
// access to `window.opener` and can navigate the preview tab to a phishing
// page). Registered once at module load — runs on every subsequent
// `DOMPurify.sanitize()` call from this app.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

interface RichTextPreviewProps extends HTMLAttributes<HTMLDivElement> {
  html: string;
  // Set when the HTML is already self-contained — i.e. it carries its own
  // `<style>` and `<div class="cd-doc">` wrapper (e.g. the backend consent
  // preview response). We then skip adding our own `cd-doc` class so the
  // wrapper/styles are not duplicated. Default surfaces (editor previews) leave
  // this off and get the `cd-doc` class + the injected base CSS.
  selfContained?: boolean;
}

// ---------------------------------------------------------------------------
// CANONICAL HTML RENDER COMPONENT
//
// Any HTML coming from the backend, a template, a variable preview, or
// anywhere outside fully-static developer-authored markup MUST be rendered
// through this component — never via `dangerouslySetInnerHTML` directly.
//
// Rationale:
//
// - Single sanitization point. We sanitize via DOMPurify here, plus apply
//   global hardening hooks (e.g. `rel="noopener noreferrer"` on
//   `target="_blank"`, see above). Bypassing this component bypasses those
//   defenses.
//
// - Defense in depth. TipTap strips dangerous nodes at edit time and Pebble
//   escapes substituted variables on the server, but a bug in either layer
//   would let XSS through. DOMPurify is the last line.
//
// - Future-proof trust model. Today the input is "ours" (backend-rendered
//   templates / variable example values we generate). The moment any of that
//   becomes dynamic — an example value derived from user data, a template
//   variable populated with a client-supplied field, an `exampleValue`
//   editable per tenant — the trust model breaks. Keeping sanitization here
//   means no one has to remember to add it on the day that lands.
const RichTextPreview = ({
  html,
  className,
  selfContained = false,
  ...props
}: RichTextPreviewProps) => {
  const sanitized = useMemo(() => DOMPurify.sanitize(html), [html]);

  return (
    <div
      className={cn(!selfContained && CD_DOC_CLASS, className)}
      dangerouslySetInnerHTML={{ __html: sanitized }}
      {...props}
    />
  );
};

export default RichTextPreview;
